import { Stack } from "@mui/material";
import { Typography } from "@mui/material";
import { IconButton } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import { resolvedStartHole } from '@moment-golf/shared';
import { effectiveRoundBool } from "./Functions/applyUniformStartHoleToViewEventData";
import { useTBContext } from "../Contexts/useTBStates";
import { FlightBadge, PreferenceBadge, TeeSheetLegend } from "./TeeSheetBadges";


interface TraditionalTeeSheetProps {
    createTeeTime: (startingTime: string, row: number) => Date;
    roundLocked?: boolean;
}

export const TraditionalTeeSheet = ({ createTeeTime, roundLocked = false }: TraditionalTeeSheetProps) => {


    const {
        viewEvent,
        viewEventData, setViewEventData,
        editMode,
        roundValue,
        factor, appTheme,
      } = useTBContext();

    if (!viewEvent) return null;

    const rowCount = Math.ceil(viewEvent.playerCap / viewEvent.rounds[roundValue - 1].regPerTeeTime);
    const roundConfig = viewEvent.rounds[roundValue - 1];
    const regsWithTeeTimesThisRound = viewEventData.registrations?.filter((r: any) =>
        r.teeTimes?.some((tt: any) => tt.round === roundValue)
    ) ?? [];
    console.log('[TraditionalTeeSheet] render', {
        eventId: viewEvent._id,
        eventName: viewEvent.eventName,
        roundValue,
        playerCap: viewEvent.playerCap,
        regPerTeeTime: roundConfig?.regPerTeeTime,
        startingTime: roundConfig?.startingTime,
        rowCount,
        registrationsTotal: viewEventData.registrations?.length ?? 0,
        registrationsWithTeeTimeThisRound: regsWithTeeTimesThisRound.length,
        sampleStoredTeeTimes: regsWithTeeTimesThisRound.slice(0, 2).map((r: any) => ({
            playerName: r.playerName,
            teeTimesForRound: r.teeTimes?.filter((tt: any) => tt.round === roundValue).map((tt: any) => tt.teeTime),
        })),
    });

    const handleDropTeeSheet = (playerData: any[], rowIndex: number) => {
        
        const updatedRegistrations = viewEventData.registrations.map((registration: any) => {
            if (playerData.some((player: any) => player._id === registration._id)) {
                const startingTime = viewEvent.rounds[roundValue - 1]?.startingTime ?? '';
                const newTeeTime = createTeeTime(startingTime, rowIndex);

                const updatedTeeTimes = registration.teeTimes ? [...registration.teeTimes] : [];
                const hole =
                    effectiveRoundBool(roundConfig.shotgunStart) || effectiveRoundBool(roundConfig.splitTees)
                        ? 1
                        : resolvedStartHole(roundConfig);
                updatedTeeTimes.push({
                    round: roundValue,
                    startingHole: hole,
                    teeTime: newTeeTime.toISOString()
                });
                
                console.log(`Updated Tee Times for ${playerData.map((player: any) => player.playerName).join(', ')}:`, updatedTeeTimes);

                return {
                    ...registration,
                    teeTimes: updatedTeeTimes
                };
            }
            return registration;
        });

        setViewEventData({ ...viewEventData, registrations: updatedRegistrations });
    }

    const getTeeTimeFilter = (rowIndex: number) => {
        const computedTeeTime = createTeeTime(viewEvent.rounds[roundValue - 1].startingTime, rowIndex);
        const computedIso = computedTeeTime.toISOString();

        const filteredTeeTimes = viewEventData.registrations.filter((registration: any) => {
            const hasTeeTimes = registration.teeTimes && Array.isArray(registration.teeTimes);
            const matches = hasTeeTimes && registration.teeTimes.some((teeTime: any) => {
                const roundMatch = teeTime.round === roundValue;
                const timeMatch = teeTime.teeTime === computedIso;
                return roundMatch && timeMatch;
            });
            return !!matches;
        });

        if (rowIndex === 0) {
            const sampleStored = (viewEventData.registrations ?? [])
                .flatMap((r: any) => (r.teeTimes ?? []).filter((tt: any) => tt.round === roundValue).map((tt: any) => tt.teeTime))
                .slice(0, 3);
            console.log('[TraditionalTeeSheet] getTeeTimeFilter', {
                rowIndex,
                computedIso,
                matchedCount: filteredTeeTimes.length,
                sampleStoredTeeTimesFromData: sampleStored,
                timeStringsMatch: sampleStored.map((stored: string) => stored === computedIso),
            });
        }

        return filteredTeeTimes;
    }

    return (
        <>
        {Array.from({ length: rowCount }).map((_, rowIndex) => {
            return (
            <Stack 
                key={`row-${rowIndex}`} 
                direction='row' 
                spacing={2}
                alignItems={'center'}
                sx={{ padding: 1, minHeight: 25*factor }} 
                onDragOver={(e) => { if (!roundLocked) e.preventDefault(); }} 
                onDrop={(e) => {
                    e.preventDefault();
                    if (roundLocked) return;
                    const dropPlayers = JSON.parse(e.dataTransfer.getData('text/plain'));
                    if (Math.max(0, viewEvent.rounds[roundValue-1].regPerTeeTime - getTeeTimeFilter(rowIndex).length) >=
                        dropPlayers.length) {
                        handleDropTeeSheet(dropPlayers, rowIndex);
                    }
                }}
            >
                <Typography sx={{ flex: 1, minHeight: viewEvent.useTeams ? 35*factor : 25*factor, alignContent: 'center', fontSize: 14*factor}}>
                    {createTeeTime(viewEvent.rounds[roundValue-1].startingTime, rowIndex)
                    .toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                    </Typography>
                    {getTeeTimeFilter(rowIndex).map((registration: any, index: number) => (
                        <Stack key={`player-${rowIndex}-${index + 1}`} direction='row' alignItems='center' justifyContent='space-between' 
                            sx={{ flex: 2, borderBottom: `1px dotted ${appTheme.palette.primary.light}`,  
                            minHeight: viewEvent.useTeams ? 35*factor : 25*factor, }}>
                            <Stack direction='column' alignItems='flex-start' spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ width: '100%', fontSize: 12*factor }}>
                                    {registration.playerName || `Player ${index + 1}`} 
                                </Typography>
                                {registration.teamName && <Typography sx={{ width: '100%', fontSize: 10*factor, fontStyle: 'italic' }}>
                                    ({registration.teamName})
                                </Typography>}
                            </Stack>
                            {(registration.division || registration.flight || registration.teeGroupFocusPreference) && (
                                <Stack direction='row' spacing={0.5} alignItems='center' sx={{ ml: 0.5, flexShrink: 0 }}>
                                    <FlightBadge
                                        division={registration.division}
                                        flight={registration.flight}
                                        divisionIndex={Math.max(0, viewEvent?.flighting?.divisions?.findIndex((d: any) => d.divisionName === registration.division) ?? 0)}
                                        factor={factor}
                                    />
                                    <PreferenceBadge
                                        preference={registration.teeGroupFocusPreference}
                                        factor={factor}
                                    />
                                </Stack>
                            )}
                            {editMode && !roundLocked && (<IconButton 
                                onClick={() => {
                                    setViewEventData((prev: any) => ({
                                        ...prev,
                                        registrations: prev.registrations.map((prevRegistration: any) => 
                                            registration._id === prevRegistration._id || registration.scoreCode === prevRegistration.scoreCode ? 
                                            { ...prevRegistration, 
                                                teeTimes: prevRegistration.teeTimes.filter((teeTime: any) => 
                                                    teeTime.round !== roundValue || 
                                                    teeTime.teeTime !== createTeeTime(viewEvent.rounds[roundValue-1].startingTime, rowIndex).toISOString()) 
                                            } 
                                            : prevRegistration
                                        )
                                    }));
                                }} 
                                size="small" 
                                sx={{ marginLeft: 1 }}
                            >
                                <ClearIcon sx={{ fontSize: 14 }} />
                            </IconButton>)}
                        </Stack>
                    ))}
                    {Array.from({ length: Math.max(0, viewEvent.rounds[roundValue-1].regPerTeeTime - getTeeTimeFilter(rowIndex).length) })
                            .map((_, blankIndex) => (
                        <Typography sx={{ flex: 2, borderBottom: `1px dotted ${appTheme.palette.primary.light}`, minHeight: viewEvent.useTeams ? 35*factor : 25*factor, fontSize: 12*factor }}
                            key={`blank-${rowIndex}-${blankIndex + 1}`}>
                            {' '}
                        </Typography>
                    ))}
            </Stack>
                );
            })
        }
        <TeeSheetLegend
            registrations={viewEventData.registrations ?? []}
            flighting={viewEvent.flighting}
            factor={factor}
        />
        </>
    )
}
